import type { ApiErrorBody, ErrorCode, FieldIssue } from '@lightbm/shared';

export type ClientErrorCode = ErrorCode | 'NETWORK_ERROR';

/** An error answered by the API (or a network failure), with its stable code. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ClientErrorCode,
    message: string,
    public readonly details?: FieldIssue[] | Record<string, unknown>,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field problems, to show next to the matching inputs. */
  get fieldIssues(): FieldIssue[] {
    return Array.isArray(this.details) ? this.details : [];
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

type SessionEndedListener = (code: ClientErrorCode) => void;
const sessionEndedListeners = new Set<SessionEndedListener>();

type MaintenanceListener = () => void;
const maintenanceListeners = new Set<MaintenanceListener>();

/** DAT-06: notified when the API answers that the app is closed while the data is restored. */
export function onMaintenance(listener: MaintenanceListener): () => void {
  maintenanceListeners.add(listener);
  return () => maintenanceListeners.delete(listener);
}

/** Notified when the API reports the session is gone (timeout, sign-out elsewhere, deactivation). */
export function onSessionEnded(listener: SessionEndedListener): () => void {
  sessionEndedListeners.add(listener);
  return () => sessionEndedListeners.delete(listener);
}

type Query = Record<string, string | number | boolean | null | undefined>;

interface RequestOptions {
  body?: unknown;
  form?: FormData;
  query?: Query;
  signal?: AbortSignal;
}

const BASE_PATH = '/api/v1';
/**
 * A 401 from these calls is about credentials ("not signed in"), not an ended session,
 * unless the API says SESSION_EXPIRED (a stale cookie found on page load).
 */
const AUTH_ENTRY_POINTS = ['/auth/login', '/auth/me'];

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE_PATH}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '')
      url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', 'The server cannot be reached');
  }

  if (response.status === 204) return undefined as T;
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as ApiErrorBody | null)?.error;
    const apiError = new ApiError(
      response.status,
      error?.code ?? 'INTERNAL_ERROR',
      error?.message ?? response.statusText,
      error?.details,
      error?.requestId,
    );
    const sessionEnded =
      response.status === 401 &&
      (!AUTH_ENTRY_POINTS.includes(path) || apiError.code === 'SESSION_EXPIRED');
    if (sessionEnded) {
      sessionEndedListeners.forEach((listener) => listener(apiError.code));
    }
    if (apiError.code === 'MAINTENANCE') maintenanceListeners.forEach((listener) => listener());
    throw apiError;
  }
  return (payload as { data: T }).data;
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    request<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body: body ?? {} }),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, { body }),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, { body }),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, { form }),
};
